/**
 * design.md §P4 / §P5 — records the assistant prepared, shown before any is written:
 * every new record, every changed field (old → new), what stays the same, and the
 * lines that cannot be used and why. Nothing happens until Confirm; then all of it
 * is written, or none.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Archive, ArrowRight, Check, FilePlus2, Pencil, Send, TriangleAlert, X } from 'lucide-react'
import * as React from 'react'

import { Button } from '@/components/ui/button'
import { Badge, StatusPill } from '@/components/ui/surface'
import { api, ApiError } from '@/lib/api'
import { useTable } from '@/lib/meta'
import { cn } from '@/lib/utils'
import { CellRenderer } from '@/render/CellRenderer'

/** An integration's action, or a table's own button (agents/action_changes.py): one
 *  row, what it will do. */
interface ActionRow {
  kind: 'action'
  line: number
  label: string
  count: number
  sample: { label: string; texts: string[] } | null
  /** A button that cannot run on some records now: which, and why. */
  left_out?: { label: string; error: string }[]
  result?: { done?: number; skipped?: number; failed?: { label: string; error: string }[]; queued?: number }
}

/** The same values on many records (data_changes.propose_bulk): a sample to show. */
interface BulkRow {
  kind: 'bulk'
  line: number
  /** How many records (the ids stay on the server). */
  count: number
  sample: { id: string; label: string; changes: Record<string, { from: unknown; to: unknown }> }[]
}

interface Row {
  kind: 'add' | 'change' | 'same' | 'archive' | 'problem'
  line: number
  id?: string
  label?: string
  values?: Record<string, unknown>
  changes?: Record<string, { from: unknown; to: unknown }>
  error?: string
}

export interface DataChange {
  id: string
  table: string
  table_label: string
  table_label_plural: string
  intent: string
  source: string
  rows: Row[] | [ActionRow] | [BulkRow]
  counts: Partial<Record<Row['kind'] | 'action' | 'missing' | 'left_out', number>>
  refs: Record<string, Record<string, string>>
  /** Rows beyond what the card is sent (data_changes.CARD_ROWS). */
  hidden?: number
  status: 'planned' | 'applying' | 'applied' | 'rejected' | 'failed' | 'expired'
  error: string | null
  /** Who asked for it when not the panel: "Claude", "ChatGPT" (connectors.md). */
  via?: string | null
  /** Sign-ins and levels (agents/access_changes.py): names for each column and value. */
  labels?: Record<string, string>
  display?: Record<string, string>
}

const STATUS = {
  planned: { tone: 'info', label: 'Waiting for you' },
  applying: { tone: 'info', label: 'Writing…' },
  applied: { tone: 'success', label: 'Done' },
  rejected: { tone: 'neutral', label: 'Rejected' },
  failed: { tone: 'danger', label: 'Not done' },
  expired: { tone: 'neutral', label: 'Expired' },
} as const

/** Rows shown before "Show all". */
const SHOWN = 8

interface CardProps {
  change: DataChange
  onSettled: (next: DataChange) => void
}

export function DataChangeCard(props: CardProps) {
  const first = props.change.rows[0]
  if (first?.kind === 'action') return <ActionCard {...props} action={first} />
  if (first?.kind === 'bulk') {
    // Many records, one change: the sample shows as ordinary changes.
    const sample = first.sample.map((s, i) => ({ ...s, kind: 'change' as const, line: i + 1 }))
    return (
      <RecordsCard {...props} rows={sample} more={first.count - sample.length} moreNote="the same change" />
    )
  }
  return (
    <RecordsCard
      {...props}
      rows={props.change.rows as Row[]}
      more={props.change.hidden ?? 0}
      moreNote="not shown, all part of Confirm"
    />
  )
}

/** How a card confirms, rejects and looks again: through sb-tools' API, or — on the
 *  card inside Claude or ChatGPT (src/card) — through that chat's connector. */
export interface ChangeTransport {
  settle: (change: DataChange, action: 'apply' | 'reject') => Promise<DataChange>
  load: (change: DataChange) => Promise<DataChange>
}

export const ChangeTransportContext = React.createContext<ChangeTransport>({
  settle: (change, action) => api.post<DataChange>(`/api/agent/data-changes/${change.id}/${action}`),
  load: (change) => api.get<DataChange>(`/api/agent/data-changes/${change.id}`),
})

/** Confirm or reject a prepared change; the open screens catch up after. */
function useSettle(change: DataChange, onSettled: (next: DataChange) => void) {
  const queryClient = useQueryClient()
  const transport = React.useContext(ChangeTransportContext)
  const [error, setError] = React.useState<string | null>(null)
  const settle = useMutation({
    mutationFn: (action: 'apply' | 'reject') => transport.settle(change, action),
    onSuccess: (next) => {
      setError(null)
      onSettled(next)
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : 'Could not do this.'),
  })
  // A large change is written by the worker: look again until it is done.
  const progress = useQuery({
    queryKey: ['data-change', change.id],
    queryFn: () => transport.load(change),
    enabled: change.status === 'applying',
    refetchInterval: 3000,
  })
  React.useEffect(() => {
    if (change.status === 'applying' && progress.data && progress.data.status !== 'applying') {
      onSettled(progress.data)
      // Written by the worker, not by a change made here: read everything again (§P24).
      void queryClient.invalidateQueries()
    }
  }, [change.status, progress.data, onSettled, queryClient])
  return { settle, error }
}

function CardHeader({ change, subtitle }: { change: DataChange; subtitle: string }) {
  const status = STATUS[change.status]
  return (
    <div className="flex items-start justify-between gap-2 border-b border-border-soft px-3 py-2">
      <div className="min-w-0">
        <p className="font-medium">{change.intent || subtitle}</p>
        <p className="truncate text-[11px] text-faint">
          {subtitle}
          {change.via ? ` · proposed by ${change.via}` : ''}
        </p>
      </div>
      <StatusPill tone={status.tone}>{status.label}</StatusPill>
    </div>
  )
}

/**
 * design.md §P4 — an integration's action the assistant prepared ("Send WhatsApp"):
 * how many it reaches, how many have no recipient, the first message as it will
 * read. Nothing leaves until Send; then it runs exactly as the action's button does.
 */
function ActionCard({ change, onSettled, action }: CardProps & { action: ActionRow }) {
  const { settle, error } = useSettle(change, onSettled)
  const reaches = change.counts.action ?? 0
  const missing = change.counts.missing ?? 0
  const leftOut = change.counts.left_out ?? 0
  const result = action.result
  return (
    <div className="overflow-hidden rounded-lg border border-border bg-surface text-xs">
      <CardHeader change={change} subtitle={`${action.label} · ${change.table_label_plural}`} />
      <div className="flex flex-wrap gap-1.5 px-3 pt-2">
        <Badge tone="info">
          {reaches} {reaches === 1 ? change.table_label.toLowerCase() : change.table_label_plural.toLowerCase()}
        </Badge>
        {missing ? <Badge tone="warn">{missing} without a recipient, left out</Badge> : null}
        {leftOut ? <Badge tone="warn">{leftOut} cannot run now, left out</Badge> : null}
      </div>
      {action.left_out?.length ? (
        <ul className="space-y-0.5 px-3 pt-1.5 text-[11px] text-muted">
          {action.left_out.map((item) => (
            <li key={item.label}>
              {item.label}: {item.error}
            </li>
          ))}
        </ul>
      ) : null}
      {action.sample ? (
        <div className="px-3 py-2">
          <p className="text-[11px] text-faint">First: {action.sample.label}</p>
          {action.sample.texts.map((text, index) => (
            <p key={index} className="mt-1 rounded-md bg-surface-2 px-2 py-1.5 whitespace-pre-wrap">
              {text}
            </p>
          ))}
        </div>
      ) : null}

      {change.error ? <p className="mx-3 mb-2 text-danger">{change.error}</p> : null}
      {error ? <p className="mx-3 mb-2 text-danger">{error}</p> : null}

      {change.status === 'planned' ? (
        <div className="flex items-center justify-between gap-2 border-t border-border-soft px-3 py-2">
          <span className="text-[11px] text-faint">Nothing happens until you confirm.</span>
          <div className="flex shrink-0 gap-1.5">
            <Button
              variant="ghost"
              size="sm"
              loading={settle.isPending && settle.variables === 'reject'}
              onClick={() => settle.mutate('reject')}
            >
              <X />
              Reject
            </Button>
            <Button
              variant="primary"
              size="sm"
              disabled={reaches === 0}
              loading={settle.isPending && settle.variables === 'apply'}
              onClick={() => settle.mutate('apply')}
            >
              <Send />
              {action.label} · {reaches}
            </Button>
          </div>
        </div>
      ) : change.status === 'applied' && result ? (
        <div className="border-t border-border-soft px-3 py-2">
          {result.queued ? (
            <p className="text-success">
              {result.queued} queued. You will be notified when they are done.
            </p>
          ) : (
            <p className="text-success">
              {result.done ?? 0} done
              {result.skipped ? `, ${result.skipped} without a recipient` : ''}
              {result.failed?.length ? `, ${result.failed.length} failed` : ''}.
            </p>
          )}
          {result.failed?.map((failure) => (
            <p key={failure.label} className="mt-1 text-danger">
              {failure.label}: {failure.error}
            </p>
          ))}
        </div>
      ) : null}
    </div>
  )
}

function RecordsCard({
  change,
  onSettled,
  rows: changeRows,
  more = 0,
  moreNote = '',
}: CardProps & { rows: Row[]; more?: number; moreNote?: string }) {
  // Sign-ins are not an app table; their card names its own columns.
  const meta = useTable(change.labels ? '' : change.table)
  const [all, setAll] = React.useState(false)
  const { settle, error } = useSettle(change, onSettled)
  const fieldOf = (slug: string) => meta.data?.fields.find((f) => f.slug === slug)
  const labelOf = (slug: string) => change.labels?.[slug] ?? fieldOf(slug)?.label ?? slug
  const value = (slug: string, raw: unknown) => {
    const field = fieldOf(slug)
    if (!field) {
      const text = raw === null || raw === undefined ? 'None' : String(raw)
      return <span>{change.display?.[text] ?? (raw === null || raw === undefined ? '—' : text)}</span>
    }
    return <CellRenderer field={field} record={{ id: '', [slug]: raw }} refs={change.refs} />
  }

  const adds = change.counts.add ?? 0
  const changes = change.counts.change ?? 0
  const same = change.counts.same ?? 0
  const archives = change.counts.archive ?? 0
  const problems = change.counts.problem ?? 0
  // Problems first: they are what the person has to act on.
  const order = { problem: 0, change: 1, add: 2, archive: 3, same: 4 }
  const rows = [...changeRows].sort((a, b) => order[a.kind] - order[b.kind] || a.line - b.line)
  const shown = all ? rows : rows.slice(0, SHOWN)

  return (
    <div className="overflow-hidden rounded-lg border border-border bg-surface text-xs">
      <CardHeader
        change={change}
        subtitle={`${change.table_label_plural}${change.source !== 'assistant' ? ` · from ${change.source}` : ''}`}
      />

      <div className="flex flex-wrap gap-1.5 px-3 pt-2">
        {adds ? <Badge tone="success">{adds} new</Badge> : null}
        {changes ? <Badge tone="info">{changes} changed</Badge> : null}
        {archives ? <Badge tone="warn">{archives} to archive</Badge> : null}
        {same ? <Badge tone="neutral">{same} unchanged</Badge> : null}
        {problems ? <Badge tone="danger">{problems} cannot be used</Badge> : null}
      </div>

      <ul className="max-h-80 space-y-1 overflow-y-auto px-3 py-2">
        {shown.map((row) => (
          <li
            key={`${row.kind}-${row.line}`}
            className={cn('rounded-md px-2 py-1.5', row.kind === 'problem' ? 'bg-danger-soft' : 'bg-surface-2')}
          >
            <p className="flex items-center gap-1.5">
              {row.kind === 'add' ? (
                <FilePlus2 className="size-3.5 shrink-0 text-success" />
              ) : row.kind === 'change' ? (
                <Pencil className="size-3.5 shrink-0 text-info" />
              ) : row.kind === 'archive' ? (
                <Archive className="size-3.5 shrink-0 text-warn" />
              ) : row.kind === 'problem' ? (
                <TriangleAlert className="size-3.5 shrink-0 text-danger" />
              ) : (
                <Check className="size-3.5 shrink-0 text-faint" />
              )}
              <span className={cn('min-w-0 truncate', row.kind === 'problem' ? 'text-danger' : 'font-medium')}>
                {row.kind === 'problem'
                  ? `Line ${row.line}: ${row.error}`
                  : `${row.kind === 'add' ? 'New: ' : row.kind === 'archive' ? 'Archive: ' : ''}${row.label ?? ''}`}
              </span>
              {row.kind === 'same' ? <span className="text-faint">· no change</span> : null}
            </p>
            {row.kind === 'change' && row.changes ? (
              <dl className="mt-1 space-y-0.5 pl-5">
                {Object.entries(row.changes).map(([slug, diff]) => (
                  <div key={slug} className="flex flex-wrap items-center gap-1.5 text-muted">
                    <dt className="text-faint">{labelOf(slug)}</dt>
                    <dd className="flex flex-wrap items-center gap-1">
                      <span className="line-through decoration-border">{value(slug, diff.from)}</span>
                      <ArrowRight className="size-3 text-faint" aria-label="to" />
                      {value(slug, diff.to)}
                    </dd>
                  </div>
                ))}
              </dl>
            ) : null}
            {row.kind === 'add' && row.values ? (
              <dl className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 pl-5 text-muted">
                {Object.entries(row.values)
                  .filter(([slug]) => slug !== (meta.data?.label_field ?? 'name'))
                  .map(([slug, raw]) => (
                    <div key={slug} className="flex items-center gap-1">
                      <dt className="text-faint">{labelOf(slug)}:</dt>
                      <dd>{value(slug, raw)}</dd>
                    </div>
                  ))}
              </dl>
            ) : null}
          </li>
        ))}
        {more > 0 ? (
          <li className="text-[11px] text-faint">…and {more.toLocaleString()} more, {moreNote}</li>
        ) : null}
        {rows.length > SHOWN ? (
          <li>
            <button
              type="button"
              onClick={() => setAll((v) => !v)}
              className="text-[11px] text-accent hover:underline"
            >
              {all ? 'Show fewer' : `Show all ${rows.length}`}
            </button>
          </li>
        ) : null}
      </ul>

      {change.error ? <p className="mx-3 mb-2 text-danger">{change.error}</p> : null}
      {error ? <p className="mx-3 mb-2 text-danger">{error}</p> : null}

      {change.status === 'planned' ? (
        <div className="flex items-center justify-between gap-2 border-t border-border-soft px-3 py-2">
          <span className="text-[11px] text-faint">
            {problems ? 'Lines that cannot be used are left out.' : 'Nothing is written until you confirm.'}
          </span>
          <div className="flex shrink-0 gap-1.5">
            <Button
              variant="ghost"
              size="sm"
              loading={settle.isPending && settle.variables === 'reject'}
              onClick={() => settle.mutate('reject')}
            >
              <X />
              Reject
            </Button>
            <Button
              variant="primary"
              size="sm"
              disabled={adds + changes + archives === 0}
              loading={settle.isPending && settle.variables === 'apply'}
              onClick={() => settle.mutate('apply')}
            >
              <Check />
              Confirm
              {adds ? ` · add ${adds}` : ''}
              {changes ? ` · change ${changes}` : ''}
              {archives ? ` · archive ${archives}` : ''}
            </Button>
          </div>
        </div>
      ) : change.status === 'applying' ? (
        <p className="border-t border-border-soft px-3 py-2 text-muted">
          Writing {(adds + changes + archives).toLocaleString()} in the background, all or none. You can
          leave this screen; you will be notified when it is done.
        </p>
      ) : change.status === 'applied' ? (
        <p className="border-t border-border-soft px-3 py-2 text-success">
          {adds ? `${adds} added` : ''}
          {adds && changes ? ', ' : ''}
          {changes ? `${changes} changed` : ''}
          {(adds || changes) && archives ? ', ' : ''}
          {archives ? `${archives} archived` : ''}.
        </p>
      ) : null}
    </div>
  )
}
